import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AIChatResponseEventFragment } from 'types/graphql'

import ContentAreaOverlay from 'src/components/ContentAreaOverlay/ContentAreaOverlay'
import {
  StaffAIChatMessage,
  useStaffAIAssistant,
} from 'src/components/StaffAIAssistant/StaffAIAssistantContext'
import { useSubscribeToAIChatSessionResponses } from 'src/fetch/aiChat'
import {
  useCreateStaffAIChatMutation,
  useGetStaffAIChatSuggestedPromptsQuery,
} from 'src/fetch/staffAIChat'
import { VStack } from 'src/govwell-ui'
import { useChatStorageIds } from 'src/hooks/use-chat-storage-ids'
import { useIsInternalStaffAIEnabled } from 'src/utils/featureFlagging/featureFlagging'

import StaffAIChatEmptyState from './StaffAIChatEmptyState'
import StaffAIChatHeader from './StaffAIChatHeader'
import StaffAIChatInputArea from './StaffAIChatInputArea'
import StaffAIChatMessageItem from './StaffAIChatMessageItem'

/**
 * Updates the last message in the messages array.
 */
const updateLastMessage = (
  update: (message: StaffAIChatMessage) => StaffAIChatMessage
): React.SetStateAction<StaffAIChatMessage[]> => {
  return (prev) => {
    const lastMessage = prev[prev.length - 1]
    if (!lastMessage) {
      return prev
    }
    return [...prev.slice(0, -1), update(lastMessage)]
  }
}

type StaffAIAssistantOverlayContentProps = {
  onClose: () => void
  messages: StaffAIChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<StaffAIChatMessage[]>>
  isAssistantTyping: boolean
  setIsAssistantTyping: React.Dispatch<React.SetStateAction<boolean>>
}

const StaffAIAssistantOverlayContent = ({
  onClose,
  messages,
  setMessages,
  isAssistantTyping,
  setIsAssistantTyping,
}: StaffAIAssistantOverlayContentProps) => {
  const [userTextInput, setUserTextInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { chatLocalStorageId, chatSessionStorageId } = useChatStorageIds()
  const { mutateAsync: createStaffAIChat } = useCreateStaffAIChatMutation()
  const { suggestedPrompts, isLoading: isSuggestedPromptsLoading } =
    useGetStaffAIChatSuggestedPromptsQuery()

  // Subscribe to streaming responses
  const onResponseEvent = useCallback((event: AIChatResponseEventFragment) => {
    if (event.__typename === 'AIChatTextUpdate') {
      setMessages(
        updateLastMessage((lastMessage) => ({
          ...lastMessage,
          text: event.fullText,
          isThinking: !event.done,
        }))
      )
    } else if (event.__typename === 'AIChatTextDelta') {
      setMessages(
        updateLastMessage((lastMessage) => ({
          ...lastMessage,
          text: lastMessage.text + (event.text || ''),
          isThinking: !event.done,
        }))
      )
    }
  }, [])

  useSubscribeToAIChatSessionResponses({
    chatSessionStorageId,
    onResponseEvent,
  })

  // Auto-scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = useCallback(
    async (autoSendText?: string) => {
      const textMessage = autoSendText ?? userTextInput
      if (!textMessage.trim() || isAssistantTyping) {
        return
      }

      // Add user message and placeholder for AI response
      setMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          text: textMessage,
          sender: 'user',
          questionCreatedAt: new Date().toISOString(),
        },
        {
          id: `ai-${Date.now()}`,
          text: '',
          sender: 'ai',
          isThinking: true,
        },
      ])
      setUserTextInput('')
      setIsAssistantTyping(true)

      try {
        const result = await createStaffAIChat({
          input: {
            chatLocalStorageId,
            chatSessionStorageId,
            question: textMessage,
            sessionType: 'Internal',
          },
        })

        // Update the last message with final response data
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            id: result.createAIChat.id,
            questionCreatedAt: result.createAIChat.questionCreatedAt ?? '',
            responseCreatedAt: result.createAIChat.responseCreatedAt ?? '',
            isThinking: false,
            ...(result.createAIChat.response ? { text: result.createAIChat.response } : {}),
            ...(result.createAIChat.citations?.length
              ? { citations: result.createAIChat.citations }
              : {}),
          }))
        )
      } catch {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: '<div>Sorry, something went wrong. Please try again.</div>',
            isThinking: false,
          }))
        )
      } finally {
        setIsAssistantTyping(false)
      }
    },
    [userTextInput, isAssistantTyping, createStaffAIChat, chatLocalStorageId, chatSessionStorageId]
  )

  const handleSelectPrompt = useCallback(
    (prompt: string) => {
      void handleSendMessage(prompt)
    },
    [handleSendMessage]
  )

  // Use prompts from backend query (no duplicate defaults needed)
  const displayPrompts = useMemo(
    () => suggestedPrompts.slice(0, 3).map((s) => s.prompt),
    [suggestedPrompts]
  )

  const hasMessages = messages.length > 0

  return (
    <div className="flex h-full flex-col bg-neutral-50">
      <StaffAIChatHeader onClose={onClose} />

      {/* Main Content Area */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {!hasMessages ? (
          <StaffAIChatEmptyState />
        ) : (
          <div className="mx-auto w-full max-w-4xl flex-1 px-6 py-4">
            <VStack gap={4} align="stretch">
              {messages.map((msg) => (
                <StaffAIChatMessageItem key={msg.id} message={msg} />
              ))}
              <div ref={messagesEndRef} />
            </VStack>
          </div>
        )}
      </div>

      <StaffAIChatInputArea
        value={userTextInput}
        onValueChange={setUserTextInput}
        onSend={() => handleSendMessage()}
        isDisabled={isAssistantTyping}
        suggestedPrompts={displayPrompts}
        onSelectPrompt={handleSelectPrompt}
        showPrompts={!hasMessages && !isSuggestedPromptsLoading}
      />
    </div>
  )
}

const StaffAIAssistantOverlay = () => {
  const isInternalStaffAIEnabled = useIsInternalStaffAIEnabled()
  const assistantContext = useStaffAIAssistant()

  // Guard: feature flag must be enabled and context must be available
  if (!isInternalStaffAIEnabled || !assistantContext) {
    return null
  }

  const { overlay, messages, setMessages, isAssistantTyping, setIsAssistantTyping } =
    assistantContext

  return (
    <ContentAreaOverlay isOpen={overlay.isOpen} onClose={overlay.close}>
      <StaffAIAssistantOverlayContent
        onClose={overlay.close}
        messages={messages}
        setMessages={setMessages}
        isAssistantTyping={isAssistantTyping}
        setIsAssistantTyping={setIsAssistantTyping}
      />
    </ContentAreaOverlay>
  )
}

export default React.memo(StaffAIAssistantOverlay)
